/**
 * File: CaseManagementDashboard.jsx
 * Path: /src/components/solutions/CaseManagementDashboard.jsx
 * Description: Standalone dashboard visualization for the Case Management AI solution
 * Key Features: Interactive case management interface with AI-powered workflow visualization
 */

import React from 'react';
import { motion } from 'framer-motion';

const CaseManagementDashboard = () => {
  return (
    <div className="relative rounded-xl overflow-hidden shadow-xl w-full">
      <div className="bg-gradient-to-br from-blue-700 to-primary-700 p-6">
        {/* Circuit pattern */}
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" stroke="rgba(255,255,255,0.3)" />
              <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" stroke="rgba(255,255,255,0.4)" />
              <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" stroke="rgba(255,255,255,0.25)" />
              <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" stroke="rgba(255,255,255,0.3)" />
            </g>
            <g fill="rgba(255,255,255,0.5)">
              <circle cx="492.5" cy="224.5" r="4" />
              <circle cx="365.5" cy="196.5" r="4" />
              <circle cx="627.5" cy="358.5" r="4" />
              <circle cx="200.5" cy="144.5" r="4" />
              <circle cx="549.5" cy="421.5" r="4" />
            </g>
          </svg>
        </div>
        
        {/* Dashboard container with min-height to prevent cutting off */}
        <div className="min-h-[440px] w-full flex items-center justify-center relative z-10">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 w-full text-white">
            {/* Interface Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl">Veritect Case Management AI</h3>
              </div>
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
            </div>
            
            {/* Case Management Interface */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              {/* Left Panel - Case List */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-3">
                  <div className="text-sm font-medium">Active Cases</div>
                  <div className="bg-blue-500/20 text-blue-200 text-xs px-2 py-0.5 rounded-full">12 Cases</div>
                </div>
                
                <div className="space-y-2">
                  {/* Active case item */}
                  <div className="bg-blue-500/20 border border-blue-300/30 rounded-lg p-2 cursor-pointer">
                    <div className="flex justify-between">
                      <div className="font-medium text-sm">Smith v. Acme Corp</div>
                      <div className="bg-green-500/30 text-green-200 text-xs px-1.5 rounded">Active</div>
                    </div>
                    <div className="text-xs text-white/70 mt-1">Personal Injury - Updated 2h ago</div>
                  </div>
                  
                  {/* Regular case items */}
                  {['Johnson IP Dispute', 'Martinez Estate Planning', 'Doe LLC Formation'].map((caseName, index) => (
                    <div key={index} className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg p-2 cursor-pointer">
                      <div className="font-medium text-sm">{caseName}</div>
                      <div className="text-xs text-white/70 mt-1">Updated {index + 1}d ago</div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Middle Panel - Case Details */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-3">
                  <div className="text-sm font-medium">Smith v. Acme Corp</div>
                  <div className="flex space-x-1">
                    <div className="bg-blue-500/20 text-blue-200 text-xs px-2 py-0.5 rounded-full flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      June 15
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-xs text-white/90 bg-white/5 p-2 rounded-lg">
                    <div className="font-medium mb-1">Case Overview</div>
                    <p>Personal injury case involving workplace accident at Acme Manufacturing plant. Client suffered back injuries requiring surgery.</p>
                  </div>
                  
                  <div className="text-xs text-white/90 bg-white/5 p-2 rounded-lg">
                    <div className="font-medium mb-1">Key Dates</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>Filing Date: <span className="text-blue-200">Mar 12, 2025</span></div>
                      <div>Response Due: <span className="text-yellow-200">May 15, 2025</span></div>
                      <div>Discovery Ends: <span className="text-blue-200">Aug 30, 2025</span></div>
                      <div>Trial Date: <span className="text-green-200">Nov 10, 2025</span></div>
                    </div>
                  </div>
                  
                  <div className="text-xs text-white/90 bg-white/5 p-2 rounded-lg">
                    <div className="font-medium mb-1">Case Team</div>
                    <div className="flex space-x-2">
                      <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-[10px]">JS</div>
                      <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center text-[10px]">MR</div>
                      <div className="w-6 h-6 rounded-full bg-pink-600 flex items-center justify-center text-[10px]">KT</div>
                      <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center text-[10px]">+2</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right Panel - AI Insights */}
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3">
                <div className="flex justify-between items-center mb-3">
                  <div className="text-sm font-medium">AI Insights</div>
                  <div className="flex space-x-1">
                    <div className="bg-indigo-500/20 text-indigo-200 text-xs px-2 py-0.5 rounded-full">Analyzing</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-xs text-white/90 bg-blue-500/20 border border-blue-300/30 p-2 rounded-lg">
                    <div className="font-medium mb-1 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Risk Assessment
                    </div>
                    <p>Based on 328 similar cases, plaintiff has a 68% chance of favorable settlement. Recommend gathering additional medical documentation.</p>
                  </div>
                  
                  <div className="text-xs text-white/90 bg-white/5 p-2 rounded-lg">
                    <div className="font-medium mb-1">Deadline Alert</div>
                    <div className="bg-yellow-500/20 text-yellow-200 px-2 py-1 rounded text-[10px]">
                      Expert witness disclosure due in 7 days
                    </div>
                  </div>
                  
                  <div className="text-xs text-white/90 bg-white/5 p-2 rounded-lg">
                    <div className="font-medium mb-1">Similar Cases</div>
                    <ul className="list-disc list-inside text-[10px] space-y-1">
                      <li>Rivera v. MegaCorp (settled $285K)</li>
                      <li>Chen v. Midwest Manufacturing ($320K verdict)</li>
                      <li>Williams v. Industrial Systems (dismissed)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Bottom Bar - Activity Feed */}
            <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 h-32">
              <div className="flex justify-between items-center mb-2">
                <div className="text-sm font-medium">Recent Activity</div>
                <div className="text-xs text-white/60">Last updated: 35 min ago</div>
              </div>
              
              <div className="space-y-2 overflow-y-auto h-[calc(100%-2rem)] pr-1">
                <div className="flex items-start text-xs">
                  <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-[8px] mr-2 mt-0.5 flex-shrink-0">JS</div>
                  <div>
                    <div className="text-white/90">Jane Smith added deposition notes for witness Dr. Thompson</div>
                    <div className="text-white/60 text-[10px]">Today at 2:45 PM</div>
                  </div>
                </div>
                
                <div className="flex items-start text-xs">
                  <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center text-[8px] mr-2 mt-0.5 flex-shrink-0">AI</div>
                  <div>
                    <div className="text-white/90">AI Assistant flagged potential discrepancy in medical records</div>
                    <div className="text-white/60 text-[10px]">Today at 11:20 AM</div>
                  </div>
                </div>
                
                <div className="flex items-start text-xs">
                  <div className="w-5 h-5 rounded-full bg-pink-600 flex items-center justify-center text-[8px] mr-2 mt-0.5 flex-shrink-0">KT</div>
                  <div>
                    <div className="text-white/90">Kevin Thompson uploaded 5 new documents to case file</div>
                    <div className="text-white/60 text-[10px]">Yesterday at 4:30 PM</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseManagementDashboard;